/**
 * OCR & Label Printing React Hooks
 * Epic 70: Okmanvolvasó & Zebra Cimke
 *
 * TanStack Query hooks for OCR scanning and label generation.
 */

import {
  generateBrotherLabel,
  generateZebraLabel,
  getOcrResult,
  scanDocument,
  type BrotherLabelInput,
  type OcrScanResult,
  type ZebraLabelInput,
} from '@/api/ocr';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

// ============================================
// QUERIES
// ============================================

export function useOcrResult(id: string | undefined) {
  const query = useQuery({
    queryKey: ['ocr', 'result', id],
    queryFn: async () => {
      if (!id) throw new Error('No ID');
      const res = await getOcrResult(id);
      return res.data;
    },
    enabled: !!id,
  });

  return {
    result: query.data as OcrScanResult | undefined,
    isLoading: query.isLoading,
    error: query.error?.message ?? null,
    refetch: query.refetch,
  };
}

// ============================================
// MUTATIONS
// ============================================

export function useScanDocument() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (imageData: string) => scanDocument(imageData),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['ocr'] });
    },
  });
}

export function useGenerateZebraLabel() {
  return useMutation({
    mutationFn: (input: ZebraLabelInput) => generateZebraLabel(input),
  });
}

export function useGenerateBrotherLabel() {
  return useMutation({
    mutationFn: (input: BrotherLabelInput) => generateBrotherLabel(input),
  });
}
